import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { publicAPI } from '../services/api';
import Icons from '../components/common/Icons';

/* ------------------------------------------------------------------ */
/*  Animated Counter Hook                                              */
/* ------------------------------------------------------------------ */
function useCountUp(end, duration = 2000, start = 0, trigger = false) {
  const [value, setValue] = useState(start);
  useEffect(() => {
    if (!trigger || !end) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start, trigger]);
  return value;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const fmt = (n) => (n || 0).toLocaleString('en-IN');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const statusColors = {
  completed: '#4caf50', in_progress: '#ff9800', published: '#2196f3',
  allocated: '#9c27b0', draft: '#9e9e9e', pending: '#9e9e9e',
  approved: '#4caf50', submitted: '#2196f3', pending_review: '#ff9800',
};
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const roleBadge = {
  csr: { label: 'CSR Partner', bg: 'linear-gradient(135deg, #c5a236, #d4b44a)' },
  agency: { label: 'Implementation / Plantation Agency', bg: 'linear-gradient(135deg, #2e7d32, #43a047)' },
  individual: { label: 'Individual Contributor', bg: 'linear-gradient(135deg, #ff9800, #ffa726)' },
  volunteer: { label: 'Volunteer', bg: 'linear-gradient(135deg, #4caf50, #66bb6a)' },
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
export default function ContributorDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  /* ---- Fetch data ---- */
  useEffect(() => {
    setLoading(true);
    setError(null);
    publicAPI.getContributorDetail(id)
      .then(res => {
        setData(res.data);
        document.title = `${res.data.contributor?.name || res.data.contributor?.organisation_name || 'Contributor'} | Haritanjali`;
      })
      .catch(err => {
        setError(err.response?.status === 404 ? 'Contributor not found' : 'Failed to load contributor details');
      })
      .finally(() => setLoading(false));
  }, [id]);

  /* ---- Stats visibility ---- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [loading]);

  /* ---- Fade-in observer ---- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, data]);

  /* ---- Lightbox keyboard nav ---- */
  const photos = data?.photos || [];
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(prev => (prev + 1) % photos.length);
      if (e.key === 'ArrowLeft') setLightbox(prev => (prev - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, photos.length]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  /* ---- Extract data ---- */
  const contributor = data?.contributor || {};
  const stats = data?.stats || {};
  const projects = data?.projects || [];
  const mapPoints = data?.map_points || [];
  const milestones = data?.milestones || [];
  const volunteerActivities = data?.volunteer_activities || [];
  const role = contributor.role;
  const badge = roleBadge[role] || roleBadge.volunteer;

  /* ---- Animated counters ---- */
  const treesCount = useCountUp(stats.total_plants || 0, 2000, 0, statsVisible);
  const projectsCount = useCountUp(stats.projects || 0, 1500, 0, statsVisible);
  const stat3 = role === 'agency'
    ? useCountUp(stats.experience || 0, 1500, 0, statsVisible)
    : useCountUp(stats.activities || 0, 1500, 0, statsVisible);

  /* ---- Loading / Error states ---- */
  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #e0e0e0', borderTopColor: '#2e7d32', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#888', fontSize: 16 }}>Loading contributor details...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, padding: 40 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>!</div>
        <h2 style={{ color: '#1b5e20', margin: 0 }}>{error}</h2>
        <Link to="/contributors" style={{ color: '#2e7d32', fontWeight: 600, fontSize: 15 }}>&larr; Back to Contributors</Link>
      </div>
    );
  }

  /* ================================================================ */
  /*  RENDER                                                            */
  /* ================================================================ */
  return (
    <>
      {/* ── Styles ── */}
      <style>{`
        .fade-section { opacity:0; transform:translateY(40px); transition:opacity 0.7s ease-out, transform 0.7s ease-out; }
        .fade-visible { opacity:1; transform:translateY(0); }
        .detail-project-card { transition: transform 0.3s, box-shadow 0.3s; }
        .detail-project-card:hover { transform:translateY(-6px); box-shadow:0 12px 32px rgba(46,125,50,0.12) !important; }
        .gallery-thumb { transition: transform 0.3s, box-shadow 0.3s; cursor:pointer; }
        .gallery-thumb:hover { transform:translateY(-4px); box-shadow:0 8px 24px rgba(0,0,0,0.15) !important; }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>

      {/* ==================== BACK BUTTON ==================== */}
      <div style={{ background: '#f5f7f5', padding: '100px 20px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Link to="/contributors" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: '#2e7d32', fontWeight: 600, fontSize: 15, textDecoration: 'none',
          }}>
            &larr; Back to Contributors
          </Link>
        </div>
      </div>

      {/* ==================== HERO BANNER ==================== */}
      <section style={{
        position: 'relative', padding: '40px 20px 60px', background: '#f5f7f5',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="fade-section" style={{
            background: '#fff', borderRadius: 24, padding: 'clamp(28px, 4vw, 48px)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
            display: 'flex', flexWrap: 'wrap', gap: 'clamp(24px, 3vw, 48px)', alignItems: 'center',
          }}>
            {/* Avatar / Logo */}
            <div style={{
              width: 100, height: 100, borderRadius: '50%', flexShrink: 0,
              border: `3px solid ${role === 'csr' ? '#c5a236' : role === 'agency' ? '#2e7d32' : role === 'individual' ? '#ff9800' : '#4caf50'}`,
              background: role === 'csr' ? '#fff8e1' : role === 'agency' ? '#e8f5e9' : role === 'individual' ? '#fff3e0' : '#e8f5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: role === 'csr' ? '#c5a236' : role === 'agency' ? '#2e7d32' : role === 'individual' ? '#ff9800' : '#4caf50',
              overflow: 'hidden',
            }}>
              {role === 'csr' ? <Icons.Building /> : role === 'agency' ? <Icons.Gear /> : <Icons.AvatarPlaceholder />}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{
                display: 'inline-block', background: badge.bg,
                color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 14px',
                borderRadius: 12, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10,
              }}>{badge.label}</div>
              <h1 style={{
                fontSize: 'clamp(24px, 3.5vw, 38px)', fontWeight: 800,
                color: '#1b5e20', lineHeight: 1.2, margin: '0 0 8px',
              }}>
                {contributor.organisation_name || contributor.name}
              </h1>
              {(contributor.city || contributor.state) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#888', fontSize: 15, marginBottom: 6 }}>
                  <Icons.MapPin /> {[contributor.city, contributor.state].filter(Boolean).join(', ')}
                </div>
              )}
              {contributor.areas_of_operation && (
                <div style={{ color: '#999', fontSize: 14, marginTop: 4 }}>
                  Areas: {contributor.areas_of_operation}
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div ref={statsRef} style={{
              display: 'flex', gap: 16, flexWrap: 'wrap',
            }}>
              {stats.total_plants > 0 && (
                <div style={quickStatStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20' }}>{fmt(treesCount)}</div>
                  <div style={quickStatLabel}>Trees</div>
                </div>
              )}
              {stats.projects > 0 && (
                <div style={quickStatStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20' }}>{fmt(projectsCount)}</div>
                  <div style={quickStatLabel}>Projects</div>
                </div>
              )}
              {role === 'agency' && stats.experience > 0 && (
                <div style={quickStatStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20' }}>{stat3}</div>
                  <div style={quickStatLabel}>Years Exp.</div>
                </div>
              )}
              {role === 'agency' && stats.rating > 0 && (
                <div style={quickStatStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#c5a236' }}>&#9733; {stats.rating.toFixed(1)}</div>
                  <div style={quickStatLabel}>Rating</div>
                </div>
              )}
              {(role === 'individual' || role === 'volunteer') && stats.activities > 0 && (
                <div style={quickStatStyle}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20' }}>{fmt(stat3)}</div>
                  <div style={quickStatLabel}>Activities</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROJECTS SECTION ==================== */}
      {projects.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#fff' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div className="fade-section" style={{ textAlign: 'center', marginBottom: 40 }}>
              <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                {role === 'csr' ? 'Plantation Projects' : role === 'agency' ? 'Assigned Projects' : 'Sponsorships'}
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', color: '#1b5e20', margin: 0 }}>
                {role === 'individual' ? 'Trees Sponsored' : 'Projects & Initiatives'}
              </h2>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 24,
            }}>
              {projects.map((p, i) => (
                <div
                  key={i}
                  className="detail-project-card fade-section"
                  style={{
                    background: '#fff', borderRadius: 16, padding: 28,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    border: '1px solid #f0f0f0',
                    transitionDelay: `${i * 0.08}s`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20', margin: 0, flex: 1 }}>
                      {p.title}
                    </h3>
                    <span style={{
                      display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 11,
                      fontWeight: 700, color: '#fff', flexShrink: 0, marginLeft: 12,
                      background: statusColors[p.status] || '#9e9e9e',
                    }}>
                      {statusLabel(p.status)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                    <div style={projectMetricStyle}>
                      <Icons.Tree />
                      <span><strong>{fmt(p.total_plants)}</strong> trees</span>
                    </div>
                    {p.plantation_type && (
                      <div style={projectMetricStyle}>
                        <Icons.Leaf />
                        <span>{p.plantation_type}</span>
                      </div>
                    )}
                  </div>

                  {p.species_details && (
                    <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                      <strong style={{ color: '#555' }}>Species:</strong> {p.species_details}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#999' }}>
                    {(p.city || p.state) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icons.MapPin /> {[p.city, p.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {(p.start_date || p.end_date) && (
                      <div>{fmtDate(p.start_date)} - {fmtDate(p.end_date)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== VOLUNTEER ACTIVITIES ==================== */}
      {volunteerActivities.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#fff' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div className="fade-section" style={{ textAlign: 'center', marginBottom: 40 }}>
              <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                Community Engagement
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', color: '#1b5e20', margin: 0 }}>
                Volunteering Activities
              </h2>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20,
            }}>
              {volunteerActivities.map((a, i) => (
                <div key={i} className="fade-section" style={{
                  background: '#fff', borderRadius: 16, padding: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  border: '1px solid #e8f5e9',
                  transitionDelay: `${i * 0.08}s`,
                }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20', margin: '0 0 8px' }}>{a.title}</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888', marginBottom: 4 }}>
                    <Icons.MapPin /> {[a.city, a.state].filter(Boolean).join(', ')}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#999', marginTop: 8 }}>
                    <span>{fmtDate(a.activity_date)}</span>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: a.signup_status === 'attended' ? '#e8f5e9' : '#fff3e0',
                      color: a.signup_status === 'attended' ? '#2e7d32' : '#ff9800',
                    }}>
                      {statusLabel(a.signup_status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== PLANTATION MAP ==================== */}
      {mapPoints.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#f5f7f5' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div className="fade-section" style={{ textAlign: 'center', marginBottom: 40 }}>
              <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                Location Footprint
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', color: '#1b5e20', margin: 0 }}>
                Plantation Map
              </h2>
            </div>
            <div className="fade-section" style={{
              height: 450, borderRadius: 20, overflow: 'hidden',
              boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
              border: '3px solid #e0e0e0',
            }}>
              <MapContainer
                center={[mapPoints[0].lat, mapPoints[0].lng]}
                zoom={mapPoints.length === 1 ? 10 : 6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {mapPoints.map((point, i) => (
                  <Marker key={i} position={[point.lat, point.lng]}>
                    <Popup>
                      <strong>{point.title}</strong><br />
                      {point.plants > 0 && <span>{fmt(point.plants)} trees</span>}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </section>
      )}

      {/* ==================== PHOTO GALLERY ==================== */}
      {photos.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#fff' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div className="fade-section" style={{ textAlign: 'center', marginBottom: 40 }}>
              <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                Visual Progress
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', color: '#1b5e20', margin: 0 }}>
                Plantation Photos
              </h2>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {photos.map((photo, i) => (
                <div
                  key={i}
                  className="gallery-thumb fade-section"
                  onClick={() => setLightbox(i)}
                  style={{
                    position: 'relative', borderRadius: 14, overflow: 'hidden',
                    aspectRatio: '4/3', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    transitionDelay: `${i * 0.06}s`,
                  }}
                >
                  <img
                    src={photo.url}
                    alt={photo.description || 'Plantation photo'}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    padding: '24px 14px 10px', color: '#fff',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{photo.description}</div>
                    {photo.captured_at && (
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{fmtDate(photo.captured_at)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== MILESTONES TIMELINE ==================== */}
      {milestones.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#f5f7f5' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <div className="fade-section" style={{ textAlign: 'center', marginBottom: 40 }}>
              <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
                Progress Tracking
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', color: '#1b5e20', margin: 0 }}>
                Milestones
              </h2>
            </div>
            <div className="fade-section" style={{ position: 'relative', paddingLeft: 40 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', left: 15, top: 0, bottom: 0,
                width: 2, background: '#c8e6c9',
                borderLeft: '2px dashed #a5d6a7',
              }} />

              {milestones.map((m, i) => {
                const color = m.status === 'approved' || m.status === 'completed' ? '#4caf50'
                  : m.status === 'in_progress' || m.status === 'submitted' ? '#ff9800'
                  : '#9e9e9e';
                return (
                  <div key={i} style={{ position: 'relative', marginBottom: 28, paddingLeft: 20 }}>
                    {/* Circle */}
                    <div style={{
                      position: 'absolute', left: -33, top: 4,
                      width: 20, height: 20, borderRadius: '50%',
                      background: color, border: '3px solid #fff',
                      boxShadow: `0 0 0 3px ${color}33`,
                    }} />
                    <div style={{
                      background: '#fff', borderRadius: 14, padding: '18px 22px',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20', margin: 0 }}>{m.title}</h4>
                        <span style={{
                          padding: '3px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                          color: '#fff', background: color,
                        }}>
                          {statusLabel(m.status)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#888' }}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 8,
                          background: '#f5f5f5', fontSize: 11, fontWeight: 600, color: '#666',
                        }}>
                          {statusLabel(m.type)}
                        </span>
                        {m.due_date && <span>Due: {fmtDate(m.due_date)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ==================== LIGHTBOX ==================== */}
      {lightbox !== null && lightbox < photos.length && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <button onClick={closeLightbox} style={{
            position: 'absolute', top: 20, right: 20,
            background: 'none', border: 'none', color: '#fff',
            fontSize: 32, cursor: 'pointer', zIndex: 10,
            width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>&times;</button>

          <button
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}
            style={lightboxNavBtn('left')}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >&#8249;</button>

          <img
            src={photos[lightbox].url}
            alt={photos[lightbox].description || ''}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8, cursor: 'default' }}
          />

          <button
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}
            style={lightboxNavBtn('right')}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >&#8250;</button>

          <div style={{
            position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            color: '#fff', fontSize: 15, fontWeight: 500,
            background: 'rgba(0,0,0,0.5)', padding: '8px 24px', borderRadius: 20, whiteSpace: 'nowrap',
          }}>
            {photos[lightbox].description || 'Photo'} &mdash; {lightbox + 1} / {photos.length}
          </div>
        </div>
      )}
    </>
  );
}

/* ── Style helpers ── */
const quickStatStyle = {
  background: 'linear-gradient(135deg, #f5f9f5, #e8f5e9)',
  borderRadius: 16, padding: '16px 22px', textAlign: 'center', minWidth: 90,
};

const quickStatLabel = {
  fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4, fontWeight: 500,
};

const projectMetricStyle = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#555',
};

const lightboxNavBtn = (side) => ({
  position: 'absolute', [side]: 20, top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
  fontSize: 28, cursor: 'pointer', width: 48, height: 48, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background 0.2s',
});
